import React, { useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import {
    CategoryFacets,
    CategoryFilterState,
    FacetOption,
} from '../../utils/productFilters';

/** Options past this are hidden behind "покажи още", so one facet cannot fill the sidebar. */
const COLLAPSED_OPTION_COUNT = 8;

/** Past this a facet gets its own search box; scrolling that many checkboxes is not filtering. */
const SEARCHABLE_OPTION_COUNT = 12;

interface FilterSectionProps {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
    /** Shown next to the title when the section has selections. */
    badge?: number;
}

const FilterSection: React.FC<FilterSectionProps> = ({ title, children, defaultOpen = true, badge }) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="border-t border-gray-200 py-3">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                className="w-full flex items-center justify-between text-left"
            >
                <span className="font-medium flex items-center gap-2">
                    {title}
                    {badge ? (
                        <span className="bg-primary-100 text-primary-700 text-xs px-1.5 py-0.5 rounded-full">
                            {badge}
                        </span>
                    ) : null}
                </span>
                <ChevronDown
                    className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>
            {open && <div className="mt-2">{children}</div>}
        </div>
    );
};

interface OptionListProps {
    options: FacetOption[];
    onToggle: (value: string) => void;
    /** Renders "24" as "24 месеца" and the like. */
    formatLabel?: (value: string) => string;
}

const OptionList: React.FC<OptionListProps> = ({ options, onToggle, formatLabel }) => {
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState('');

    const searchable = options.length > SEARCHABLE_OPTION_COUNT;
    const term = search.trim().toLowerCase();
    const matching = term
        ? options.filter(option => option.value.toLowerCase().includes(term))
        : options;

    // A ticked option stays reachable even when the collapsed list would cut it
    // off, so a shopper can always untick what they ticked.
    const visible = expanded || term
        ? matching
        : matching.filter((option, index) => index < COLLAPSED_OPTION_COUNT || option.selected);
    const hiddenCount = matching.length - visible.length;

    return (
        <div>
            {searchable && (
                <div className="relative mb-2">
                    <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Търсене..."
                        aria-label="Търсене в опциите"
                        className="w-full border border-gray-300 rounded pl-7 pr-2 py-1 text-sm"
                    />
                </div>
            )}

            {/*
              * Only a list that has been opened up scrolls. A collapsed one is
              * short enough to stand on its own, and capping it there would put
              * the options "покажи още" reveals below the fold of a box that is
              * already full - the click would look like it did nothing.
              */}
            <div className={`space-y-1 ${expanded || term ? 'max-h-96 overflow-y-auto' : ''}`}>
                {visible.map(option => (
                    <label
                        key={option.value}
                        className="flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                        <span className="flex items-center min-w-0">
                            <input
                                type="checkbox"
                                className="mr-2 shrink-0 text-primary-600 focus:ring-primary-500"
                                checked={option.selected}
                                onChange={() => onToggle(option.value)}
                            />
                            <span className="text-sm truncate" title={option.value}>
                                {formatLabel ? formatLabel(option.value) : option.value}
                            </span>
                        </span>
                        <span className="text-xs text-gray-400 shrink-0">{option.count}</span>
                    </label>
                ))}

                {visible.length === 0 && (
                    <p className="text-xs text-gray-500 p-1">Няма съвпадения</p>
                )}
            </div>

            {!term && hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="text-xs text-primary-600 hover:text-primary-700 mt-1"
                >
                    Покажи още ({hiddenCount})
                </button>
            )}
            {!term && expanded && matching.length > COLLAPSED_OPTION_COUNT && (
                <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    className="text-xs text-primary-600 hover:text-primary-700 mt-1"
                >
                    Покажи по-малко
                </button>
            )}
        </div>
    );
};

interface CategoryFiltersProps {
    filters: CategoryFilterState;
    facets: CategoryFacets;
    onChange: (filters: CategoryFilterState) => void;
    onClear: () => void;
    activeCount: number;
    /** Closes the sheet the filters open in on a phone. */
    onClose?: () => void;
}

/**
 * The category sidebar: the fixed filters every category has, followed by the
 * spec filters the backend picked for this one.
 */
const CategoryFilters: React.FC<CategoryFiltersProps> = ({
    filters,
    facets,
    onChange,
    onClear,
    activeCount,
    onClose,
}) => {
    const toggleIn = (values: string[], value: string): string[] =>
        values.includes(value) ? values.filter(v => v !== value) : [...values, value];

    const toggleManufacturer = (manufacturer: string) =>
        onChange({ ...filters, manufacturers: toggleIn(filters.manufacturers, manufacturer) });

    const toggleWarranty = (months: string) => {
        const value = Number(months);
        onChange({
            ...filters,
            warranties: filters.warranties.includes(value)
                ? filters.warranties.filter(w => w !== value)
                : [...filters.warranties, value],
        });
    };

    const toggleAttribute = (name: string, value: string) => {
        const kept = toggleIn(filters.attributes[name] ?? [], value);
        const attributes = { ...filters.attributes };
        if (kept.length > 0) attributes[name] = kept;
        else delete attributes[name];
        onChange({ ...filters, attributes });
    };

    const setPrice = (bound: 'priceMin' | 'priceMax', raw: string) => {
        const value = raw === '' ? null : parseFloat(raw);
        onChange({ ...filters, [bound]: value !== null && Number.isFinite(value) ? value : null });
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:sticky md:top-4 md:max-h-[calc(100vh-2rem)] md:overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold flex items-center gap-2">
                    Филтри
                    {activeCount > 0 && (
                        <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full">
                            {activeCount}
                        </span>
                    )}
                </h2>
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Затвори филтрите"
                        className="md:hidden text-gray-500 hover:text-gray-700"
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>

            <div className="relative">
                <Search className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                    type="text"
                    value={filters.query}
                    onChange={e => onChange({ ...filters, query: e.target.value })}
                    placeholder="Търсене в категорията"
                    aria-label="Търсене в категорията"
                    className="w-full border border-gray-300 rounded pl-8 pr-2 py-1.5 text-sm"
                />
            </div>

            <FilterSection title="Наличност">
                <label className="flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <span className="flex items-center">
                        <input
                            type="checkbox"
                            className="mr-2 text-primary-600 focus:ring-primary-500"
                            checked={filters.inStockOnly}
                            onChange={() => onChange({ ...filters, inStockOnly: !filters.inStockOnly })}
                        />
                        <span className="text-sm">Само налични</span>
                    </span>
                    <span className="text-xs text-gray-400">{facets.inStockCount}</span>
                </label>
                <label className="flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <span className="flex items-center">
                        <input
                            type="checkbox"
                            className="mr-2 text-primary-600 focus:ring-primary-500"
                            checked={filters.promoOnly}
                            onChange={() => onChange({ ...filters, promoOnly: !filters.promoOnly })}
                        />
                        <span className="text-sm">В промоция</span>
                    </span>
                    <span className="text-xs text-gray-400">{facets.promoCount}</span>
                </label>
            </FilterSection>

            <FilterSection title="Цена (€)">
                <div className="flex items-center space-x-2">
                    <input
                        type="number"
                        inputMode="decimal"
                        placeholder={facets.priceBounds ? String(facets.priceBounds.min) : 'От'}
                        aria-label="Цена от"
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        value={filters.priceMin ?? ''}
                        onChange={e => setPrice('priceMin', e.target.value)}
                    />
                    <span className="text-gray-500">-</span>
                    <input
                        type="number"
                        inputMode="decimal"
                        placeholder={facets.priceBounds ? String(facets.priceBounds.max) : 'До'}
                        aria-label="Цена до"
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        value={filters.priceMax ?? ''}
                        onChange={e => setPrice('priceMax', e.target.value)}
                    />
                </div>
            </FilterSection>

            {facets.manufacturers.length > 0 && (
                <FilterSection title="Производител" badge={filters.manufacturers.length}>
                    <OptionList options={facets.manufacturers} onToggle={toggleManufacturer} />
                </FilterSection>
            )}

            {facets.warranties.length > 1 && (
                <FilterSection title="Гаранция" badge={filters.warranties.length}>
                    <OptionList
                        options={facets.warranties}
                        onToggle={toggleWarranty}
                        formatLabel={months => `${months} месеца`}
                    />
                </FilterSection>
            )}

            {facets.attributes.map(facet => (
                <FilterSection
                    key={facet.name}
                    title={facet.name}
                    badge={(filters.attributes[facet.name] ?? []).length}
                >
                    {/*
                      * Each supplier names this spec its own way, so most are
                      * carried by only one of them. Saying so up front is the
                      * difference between a narrow filter and a broken one.
                      */}
                    {facet.withSpec < facet.total && (
                        <p className="text-xs text-gray-500 mb-2">
                            Посочена за {facet.withSpec} от {facet.total} продукта
                        </p>
                    )}
                    <OptionList
                        options={facet.options}
                        onToggle={value => toggleAttribute(facet.name, value)}
                    />
                </FilterSection>
            ))}

            {activeCount > 0 && (
                <button
                    type="button"
                    onClick={onClear}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded mt-4 text-sm transition-colors"
                >
                    Изчисти всички филтри
                </button>
            )}
        </div>
    );
};

export default CategoryFilters;
